import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ImportLeadsModal } from '@/components/leads/import-leads-modal';
import { AddLeadModal } from '@/components/leads/add-lead-modal';
import { leadsAPI } from '@/lib/api';
import { Lead, LeadStatus, LeadSource, LeadsResponse } from '@/types/leads';
import { Users, Upload, UserPlus, Search, Filter, ChevronLeft, ChevronRight, MoreHorizontal, Mail, Phone, Globe } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useTeams } from '@/hooks/use-teams';
import { formatNumber } from '@/lib/utils';

export function LeadsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | 'all'>('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const { user } = useAuthStore();
  const { currentTeam, isLoading: teamsLoading, hasTeams, getCurrentTeamId } = useTeams();

  const teamId = getCurrentTeamId();

  const { data: leadsResponse, isLoading: leadsLoading, error } = useQuery({
    queryKey: ['leads', teamId, currentPage, searchTerm, statusFilter, sourceFilter],
    queryFn: () => leadsAPI.getLeads(teamId!, { 
      page: currentPage, 
      limit: 20, 
      ...(searchTerm && { search: searchTerm }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(sourceFilter !== 'all' && { source: sourceFilter }),
    }),
    enabled: !!teamId, // Only run query if we have a teamId
  });

  const isLoading = teamsLoading || leadsLoading;

  const leadsData = leadsResponse?.data as LeadsResponse | undefined;

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleAddLead = () => {
    setShowAddModal(true);
  };

  const getStatusColor = (status: LeadStatus) => {
    const colors = {
      [LeadStatus.NEW]: 'bg-blue-100 text-blue-800',
      [LeadStatus.CONTACTED]: 'bg-yellow-100 text-yellow-800',
      [LeadStatus.QUALIFIED]: 'bg-green-100 text-green-800',
      [LeadStatus.PROPOSAL]: 'bg-purple-100 text-purple-800',
      [LeadStatus.NEGOTIATION]: 'bg-orange-100 text-orange-800',
      [LeadStatus.CLOSED_WON]: 'bg-emerald-100 text-emerald-800',
      [LeadStatus.CLOSED_LOST]: 'bg-red-100 text-red-800',
      [LeadStatus.UNQUALIFIED]: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load leads</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!teamsLoading && !hasTeams) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No teams found</h3>
          <p className="text-muted-foreground mb-4">You need to be a member of a team to manage leads</p>
          <Button onClick={() => window.location.href = '/teams'}>
            Create or Join Team
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Leads</h1>
          <p className="text-muted-foreground">Manage and track your prospects</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleImport}>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button onClick={handleAddLead}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Search and filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value: string) => setStatusFilter(value as LeadStatus | 'all')}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.values(LeadStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={(value: string) => setSourceFilter(value as LeadSource | 'all')}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {Object.values(LeadSource).map((source) => (
                  <SelectItem key={source} value={source}>
                    {source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            All Leads ({formatNumber(leadsData?.pagination?.total || 0)})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {leadsData?.data?.map((lead: Lead) => (
              <div key={lead.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">
                        {lead.name || lead.email}
                      </h3>
                      {lead.company && (
                        <p className="text-sm text-muted-foreground truncate">
                          {lead.company}
                          {lead.jobTitle && ` • ${lead.jobTitle}`}
                        </p>
                      )}
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-muted-foreground flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {lead.email}
                        </span>
                        {lead.phone && (
                          <span className="text-xs text-muted-foreground flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {lead.phone}
                          </span>
                        )}
                        {lead.website && (
                          <span className="text-xs text-muted-foreground flex items-center">
                            <Globe className="h-3 w-3 mr-1" />
                            {lead.website}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {lead.budgetEstimate && (
                    <div className="text-right">
                      <p className="text-sm font-medium">${formatNumber(lead.budgetEstimate)}</p>
                      <p className="text-xs text-muted-foreground">Budget</p>
                    </div>
                  )}
                  <div className="text-right">
                    <Badge className={getStatusColor(lead.status)}>
                      {lead.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {(!leadsData?.data || leadsData.data.length === 0) && (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No leads found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== 'all' || sourceFilter !== 'all' 
                    ? 'Try adjusting your filters or search terms'
                    : 'Get started by importing your first leads'
                  }
                </p>
                <Button onClick={handleImport}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Leads
                </Button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {leadsData?.pagination && leadsData.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {((leadsData.pagination.page - 1) * leadsData.pagination.limit) + 1} to{' '}
                {Math.min(leadsData.pagination.page * leadsData.pagination.limit, leadsData.pagination.total)} of{' '}
                {formatNumber(leadsData.pagination.total)} leads
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {leadsData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= leadsData.pagination.totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {teamId && (
        <>
          <ImportLeadsModal 
            open={showImportModal} 
            onClose={() => setShowImportModal(false)}
            teamId={teamId}
          />
          <AddLeadModal 
            open={showAddModal} 
            onClose={() => setShowAddModal(false)}
            teamId={teamId}
          />
        </>
      )}
    </div>
  );
}